import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../store'
import { loginUser } from '../../store/slices/authSlice'

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { loading, isAuthenticated, user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    if (isAuthenticated && user) navigate(user.role === 'dev' ? '/companies' : '/dashboard', { replace: true })
  }, [isAuthenticated, user, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const result = await dispatch(loginUser(formData))
    if (!loginUser.fulfilled.match(result)) {
      setError('Invalid credentials. Please try again.')
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#faf9f7',
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        {/* Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, justifyContent: 'center' }}>
          <Box
            component="img"
            src="/hisaab-logo.svg"
            alt="Hisaab logo"
            sx={{ width: 40, height: 40, flexShrink: 0 }}
          />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#2c2a26', lineHeight: 1.2 }}>
              Hisaab
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#7a766f', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Admin Panel
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid #e8e4dd',
            borderRadius: '8px',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: '#2c2a26' }}>
            Sign in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your credentials to continue
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 1, py: 1.25 }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}

export default LoginPage
